import length from "@turf/length";
import { lineString } from "@turf/turf";
import { Cable } from "../model/viewModel/cable";
import * as cable from "../constants/cable";
import * as alert from "../constants/alert";
import * as statusConstants from "../constants/status";

const CODE_W101 = "W101";
const CODE_W102 = "W102";
const CODE_W103 = "W103";
const CODE_W104 = "W104";
const CODE_E101 = "E101";
const CODE_E102 = "E102";

export const checkOverheadLineLength = (c: Cable, reference: any, clientSettings: any) => {
  let warning = {};

  if (!clientSettings.features.CostingEnabled || c.status === statusConstants.EXISTING) {
    return warning;
  }

  const cableLength =
    c.overrideLength !== null && c.overrideLength > 0 ? c.overrideLength : c.length;

  if (c.cableGroup === cable.GROUP_MAINS_OH) {
    if (cableLength > reference.maxOHCableLengthsInMetres.mains) {
      warning = {
        code: CODE_W101,
        level: alert.LEVEL_WARNING,
        link: c.id,
        description: `Overhead mains conductor(s) exceed the maximum span length (${reference.maxOHCableLengthsInMetres.mains}m).`,
      };
    }
  }
  if (c.cableGroup === cable.GROUP_SERVICE_OH) {
    if (
      c.cableType === cable.TYPE_ABC35 &&
      cableLength > reference.maxOHCableLengthsInMetres.serviceABC35
    ) {
      warning = {
        code: CODE_W102,
        level: alert.LEVEL_WARNING,
        link: c.id,
        description: `Overhead 'ABC 35' service conductor(s) exceed the maximum span length (${reference.maxOHCableLengthsInMetres.serviceABC35}m).`,
      };
    } else if (
      c.numberOfPhases === cable.SINGLE_PHASE &&
      cableLength > reference.maxOHCableLengthsInMetres.singlePhaseService
    ) {
      warning = {
        code: CODE_W103,
        level: alert.LEVEL_WARNING,
        link: c.id,
        description: `Overhead single phase service conductor(s) exceed the maximum span length (${reference.maxOHCableLengthsInMetres.singlePhaseService}m).`,
      };
    } else if (
      c.numberOfPhases !== cable.SINGLE_PHASE &&
      c.cableType !== cable.TYPE_ABC35 &&
      cableLength > reference.maxOHCableLengthsInMetres.service
    ) {
      warning = {
        code: CODE_W104,
        level: alert.LEVEL_WARNING,
        link: c.id,
        description: `Overhead service conductor(s) exceed the maximum span length (${reference.maxOHCableLengthsInMetres.service}m).`,
      };
    }
  }

  return warning;
};

export const checkCableHasGroundTypeOverrides = (c: Cable) => {
  let error = {};

  if (!c.groundTypeOverrides || c.groundTypeOverrides.length === 0) {
    error = {
      code: CODE_E101,
      level: alert.LEVEL_ERROR,
      link: c.id,
      description: "Cable does not have ground type overrides",
    };
  }

  return error;
};

export const checkCableLengthMatchesGroundTypeOverrides = (c: Cable) => {
  let error = {};

  const groundTypeOverridesLength = c.groundTypeOverrides
    .map((g) => g.length)
    .reduce((l1, l2) => parseFloat(l1 ?? 0) + parseFloat(l2 ?? 0));

  const overrideLength = c.overrideLength > 0 ? c.overrideLength : c.length;

  if (
    parseFloat(overrideLength as any).toFixed(2) !==
    parseFloat(groundTypeOverridesLength).toFixed(2)
  ) {
    error = {
      code: CODE_E102,
      level: alert.LEVEL_ERROR,
      link: c.id,
      description: "Cable length is not equal to ground type overrides",
    };
  }

  return error;
};

export const getWarningMessage = (errors: any[], reference: any) => {
  let message =
    "The following warnings have been found. Press OK to continue with the assessment or Cancel to amend the network.";

  if (errors.length > 1) return message;

  switch (errors[0].code) {
    case CODE_W101:
      message = `Mains overhead conductor exceeds the maximum span length (${reference.maxOHCableLengthsInMetres.mains}m). Press OK to continue or Cancel to amend the network;`;
      break;
    case CODE_W102:
      message = `'ABC 35' overhead service conductor exceeds the maximum span length (${reference.maxOHCableLengthsInMetres.serviceABC35}m). Press OK to continue or Cancel to amend the network;`;
      break;
    case CODE_W103:
      message = `Single phase service overhead conductor exceeds the maximum span length (${reference.maxOHCableLengthsInMetres.singlePhaseService}m). Press OK to continue or Cancel to amend the network;`;
      break;
    case CODE_W104:
      message = `Service overhead conductor exceeds the maximum span length (${reference.maxOHCableLengthsInMetres.service}m). Press OK to continue or Cancel to amend the network;`;
      break;
    default:
      message = `${errors[0].description} Press OK to continue or Cancel to amend the network;`;
  }
  return message;
};

// Returns a list of assetIds where each asset is either a tee (branch) location or an end of feeder.
export const getAssetIdsOfTeesAndEndpoints = (cables: Cable[]): string[] => {
  // Dictionary which maps each assetId to the number of cables joined to the asset
  let joinCounts = {};
  let assetIds: string[] = [];

  cables
    .flatMap((cable) => [cable.startAssetId, cable.endAssetId])
    .forEach((assetId) => {
      if (joinCounts.hasOwnProperty(assetId)) {
        (joinCounts as any)[assetId]++;
      } else {
        (joinCounts as any)[assetId] = 1;
      }
    });

  for (var assetId in joinCounts) {
    var joinCount = (joinCounts as any)[assetId];
    // Endpoints are nodes connected to one cable
    // Tees are nodes connected to more than two cables
    if (joinCount !== 2) {
      assetIds.push(assetId);
    }
  }

  return assetIds;
};

export const getResolvedLengthString = (cable: Cable): string => {
  return parseFloat(cable.overrideLength as any)
    ? cable.overrideLength.toString()
    : parseFloat(cable.length as any).toFixed(2);
};

// The start and end of the cable indicates which way round it was drawn.
// The upstream end can only be determined after running a valid assessment.
export const getStartIsUpstream = (cable: Cable): boolean => {
  if (!!cable.startAssetId && !!cable.upstreamAssetId) {
    return cable.startAssetId === cable.upstreamAssetId;
  }
  // Default to true which is the way cables are normally drawn
  return true;
};

export const getNewCableSourceId = (existingSourceIds: string[]): string => {
  while (true) {
    // This ID only needs to be unique with a study, not globally.
    // Pseudo random number ensures each cable can be found easily when searching, unlike simple count.
    // For consistency, this function generates all ids starting "c1" and a total of 11 chars.
    // Prepending "c" for "conductor", to distinguish from nged-assigned wpdIds and other asset ids
    const random = 1000000000 + Math.floor(Math.random() * 99999999) + 1;
    const newSourceId = "c" + random.toString();
    if (!existingSourceIds.includes(newSourceId)) {
      return newSourceId;
    }
  }
};

// Where possible, keep the original ID as part of the new ID.
export const getNewCableSourceIdForSplitCable = (
  existingSourceIds: string[],
  originalSourceId: string,
  isEnd: boolean,
): string => {
  if (originalSourceId?.length < 1) {
    return getNewCableSourceId(existingSourceIds);
  }
  let unsplitId: string = originalSourceId;
  const dashIndex = originalSourceId.lastIndexOf("-");
  if (dashIndex > 0) {
    unsplitId = originalSourceId.substring(0, dashIndex);
  }
  const siblingIds = existingSourceIds.filter((id) => id.startsWith(unsplitId + "-"));
  let splitIndex =
    Math.max(
      ...siblingIds.map((id) => {
        const parts = id.split("-");
        const indexPart = parts[parts.length - 1];
        const index = parseInt(indexPart, 10);
        return isNaN(index) ? 0 : index;
      }),
      0,
    ) + 1;

  // When a cable is split, both ids may get generated before either has been stored.
  // Hence use even number for upsteam part and odd for downstream to ensure uniqueness.

  if (isEnd && splitIndex % 2 !== 0) {
    splitIndex++;
  }
  const newSourceId = `${unsplitId}-${splitIndex}`;
  return newSourceId;
};

const parseFloatProperty = (value: any): number => (value ? parseFloat(value) : 0);

// Additional Lengths are located at the upstream node and are not part of the line visible on the map.
export const getTotalAdditionalUpstreamLength = (cable: Cable): number => {
  const additionalUpstreamLength =
    parseFloatProperty(cable.additionalUpstreamPoleTermLength) +
    parseFloatProperty(cable.additionalUpstreamServiceConnectionLength);
  return additionalUpstreamLength;
};

// Additional Lengths are located at the downstream node and are not part of the line visible on the map.
export const getTotalAdditionalDownstreamLength = (cable: Cable): number => {
  const additionalDownstreamLength =
    parseFloatProperty(cable.additionalDownstreamPoleTermLength) +
    parseFloatProperty(cable.additionalDownstreamServiceConnectionLength);
  return additionalDownstreamLength;
};

export const getTotalAdditionalLength = (cable: Cable): number => {
  return getTotalAdditionalUpstreamLength(cable) + getTotalAdditionalDownstreamLength(cable);
};

// Returns the cable's 'length' property
// This may not match the geographic length, as the length property may be changed when processing a PoC.
// The geographic length may also include cable repointings
const getLength = (cable: Cable): number => {
  const overrideLength = parseFloatProperty(cable.length);
  return overrideLength;
};

const getOverrideLength = (cable: Cable): number => {
  const overrideLength = parseFloatProperty(cable.overrideLength);
  return overrideLength;
};

// Ignores any override length setting and returns the geographic length based on map coordinates.
// Ignores any additional lengths at the ends due to Pole Terms, Service Connections, etc.
export const getGeographicCableLengthExcludingAdditions = (cable: Cable): number => {
  const line = lineString(cable.geometry.map((a) => [a.lng, a.lat]));
  const regularCableLength = length(line, { units: "meters" });
  return regularCableLength;
};

// Ignores any override length setting and returns the geographic length based on map coordinates.
// Adds on any additional lengths due to Pole Terms, Service Connections, etc.
export const getGeographicCableLengthIncludingAdditions = (cable: Cable): number => {
  const regularCableLength = getGeographicCableLengthExcludingAdditions(cable);
  const additionalLength = getTotalAdditionalLength(cable);
  return regularCableLength + additionalLength;
};

// Returns the override length if specified, otherwise uses the length property.
// The length property is usually the same as the geographic length unless it has been changed when processing a PoC.
// Ignores any additional lengths at the ends due to Pole Terms, Service Connections, etc.
export const getResolvedCableLengthExcludingAdditions = (cable: Cable): number => {
  const overrideLength = getOverrideLength(cable);
  if (overrideLength > 0) return overrideLength;
  const length = getLength(cable);
  return length;
};

// Uses the override length if specified, otherwise uses the length property.
// Adds on any additional lengths due to Pole Terms, Service Connections, etc.
export const getResolvedCableLengthIncludingAdditions = (cable: Cable): number => {
  const regularCableLength = getResolvedCableLengthExcludingAdditions(cable);
  const additionalLength = getTotalAdditionalLength(cable);
  return regularCableLength + additionalLength;
};
